import { FacilityAdditionalContactEditForm } from 'application/facility/forms';
import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { FacilityAdditionalContactService } from 'application/facility/services';
import { ContactDepartmentService } from 'application/contact-department/services';
import { action } from 'mobx';
import _ from 'lodash';
import { LookupService } from "common/services";
import { emailRecipientLabels } from 'core/utils';

class FacilityAdditionalContactEditViewStore extends BaseEditViewStore {
    constructor(rootStore, componentParams) {
        const facilityId = rootStore.routerStore.routerState.params.id;
        const service = new FacilityAdditionalContactService(rootStore.application.baasic.apiClient);
        const contactDepartmentService = new ContactDepartmentService(rootStore.application.baasic.apiClient);
        const additionalContact = componentParams.modalParams.data.item;
        const additionalContacts = componentParams.modalParams.data.additionalContacts;

        super(rootStore, {
            name: 'facility-additional-contact',
            id: additionalContact != undefined ? additionalContact.id : null,
            Form: FacilityAdditionalContactEditForm,
            actions: () => {
                return {
                    get: async (id) => {                                                                        
                        const response = await service.get(id, {embed : "FacilityAdditionalContactContactDepartments"});
                        response.data.facilityAdditionalContactContactDepartments = response.data.facilityAdditionalContactContactDepartments.map(i => {return i.contactDepartment });
                        return response.data;
                    },
                    update: async (resource) => {
                        resource.facilityAdditionalContactContactDepartments = resource.facilityAdditionalContactContactDepartments && resource.facilityAdditionalContactContactDepartments.map(i => {return {contactDepartmentId:i.id}});
                        if (resource.deliveryTicketType === 'To' && !resource.inActive && this.deliveryTicketToSettingExists(resource.id)){
                            resource.deliveryTicketType = 'CC';
                            rootStore.notificationStore.error("Unable to set Email settings to “To”, since the Delivery Ticket is already being sent “To” a different active contact. Email setting has been updated to “CC”.");
                        }
                        const response = await service.update(resource);
                        return response.data;
                    },
                    create: async (resource) => {
                        resource.facilityAdditionalContactContactDepartments = resource.facilityAdditionalContactContactDepartments && resource.facilityAdditionalContactContactDepartments.map(i => {return {contactDepartmentId:i.id}});
                        if (resource.deliveryTicketType === 'To' && this.deliveryTicketToSettingExists(resource.id)){
                            resource.deliveryTicketType = 'CC';
                            rootStore.notificationStore.error("Unable to set Email settings to “To”, since the Delivery Ticket is already being sent “To” a different active contact. Email setting has been updated to “CC”.");
                        }
                        const response = await service.create(_.extend(resource, {facilityId: facilityId}));
                        return response.data;
                    }
                }
            },
            
            FormClass: FacilityAdditionalContactEditForm,
        });

        this.componentParams = componentParams;
        this.additionalContacts = additionalContacts;
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);

        this.contactDepartmentStore = new BaasicDropdownStore({ multi: true, filterable: true }, {
			fetchFunc: async (term) => {
                let contactDepartmentTypeLookup = await this.lookupService.getLookups('ContactDepartmentType');
                let facilityContactType = contactDepartmentTypeLookup.contactDepartmentType.find(x => x.name === "Facility Contact");
				const response = await contactDepartmentService.find({ searchQuery: term, rpp: 50, facilityContactTypeId: facilityContactType.id });
				return response.data.item;
			},
            onChange: () => { this.setEmailSettings() }
		});
    } 

    @action.bound
    async addSave() {
        await this.form.validate();
        if(this.form.isValid){
            this.addSaveResource(this.form.values())
        }
        this.form.showErrors();
    }

    @action.bound
    async addSaveResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.create(resource);

            this.form.setFieldsDisabled(false);
            this.componentParams.modalParams.data.onSuccess();
            this.form.clear();
            this.form.$('facilityAdditionalContactContactDepartments').set([]);
           
            await setTimeout(() => this.rootStore.notificationStore.success("Contact successfully added."));
        }
        catch(err) {
            this.form.setFieldsDisabled(false);
            return this.onCreateError(err);
        }
    }

    @action.bound
    async updateResource(resource) {
        if (!this.actions.update) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.update({
                id: this.id,
                ...resource
            });

            this.form.setFieldsDisabled(false);
            this.componentParams.modalParams.data.onSuccess();
            this.componentParams.modalParams.close();
            await setTimeout(() => this.rootStore.notificationStore.success("Contact successfully updated."));
        }
        catch(err) {
            this.form.setFieldsDisabled(false);
            return this.onUpdateError(err);
        }
    }

    @action.bound
    async createResource(resource) {
        if (!this.actions.create) return;

        this.form.setFieldsDisabled(true);
        try {
            if (this.translationStore) {
                this.translationStore.applyMetadata(resource);
            }

            await this.actions.create(resource);

            this.form.setFieldsDisabled(false);
            this.componentParams.modalParams.data.onSuccess();
            this.componentParams.modalParams.close();
            await setTimeout(() => this.rootStore.notificationStore.success("Contact successfully added."));
        }
        catch(err) {
            this.form.setFieldsDisabled(false);
            return this.onCreateError(err);
        }
    }

    @action.bound
    setEmailSettings() {
        let selectedDepartments = this.form.$("facilityAdditionalContactContactDepartments").value;
        let emailOptionValues = { "To": 2, "CC": 1, "": 0 };

        emailRecipientLabels.forEach(emailSetting => {
            if (selectedDepartments.length === 0){
                this.form.$(emailSetting.name).set("");
                return;
            }

            let allDepartmentValue = selectedDepartments.map(d => d[emailSetting.name]);
            let settingValue = allDepartmentValue.reduce(function(i, j) {
                return emailOptionValues[i] > emailOptionValues[j] ? i : j;
            });

            this.form.$(emailSetting.name).set(settingValue);
        });
    }

    @action.bound
    deliveryTicketToSettingExists(id) {
        return this.additionalContacts.filter(a => a.deliveryTicketType === 'To' && !a.inActive && id !== a.id).length > 0;
    }
}

export default FacilityAdditionalContactEditViewStore;